import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Drawer,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { epcEstimationService } from '@/services/epcEstimation.service';
import { formatMoney } from '@/utils/format';

const { Text, Title } = Typography;

const INDIRECT_CATEGORIES = [
  'PROJECT_MANAGEMENT',
  'ENGINEERING',
  'CONSTRUCTION_MANAGEMENT',
  'SITE_ESTABLISHMENT',
  'TEMPORARY_FACILITIES',
  'SITE_OFFICE',
  'ACCOMMODATION',
  'TRANSPORT',
  'UTILITIES',
  'COMMUNICATION',
  'SECURITY',
  'QA_QC',
  'HSE',
  'DOCUMENT_CONTROL',
  'SURVEY',
  'TESTING_SUPPORT',
  'COMMISSIONING_SUPPORT',
  'INSURANCE_COST',
  'BOND_COST',
  'PERMITS',
  'MOBILIZATION',
  'DEMOBILIZATION',
  'CORPORATE_OVERHEAD_ALLOWANCE',
  'OTHER_INDIRECT',
];

const RISK_TREATMENTS = [
  'NOT_PRICED',
  'PRICED_EXPLICITLY',
  'COVERED_BY_CONTINGENCY',
  'TRANSFERRED',
  'ACCEPTED',
  'EXCLUDED',
];

export interface Wave5Ctx {
  estimateId: string;
  revisionId: string;
  readOnly: boolean;
}

function money(n: unknown) {
  if (n == null || n === '') return '—';
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return formatMoney(v, { display: 'plain', maximumFractionDigits: 2 });
}

export function useWave5Queries(ctx: Wave5Ctx | null) {
  const enabled = !!ctx?.estimateId && !!ctx?.revisionId;
  const totalQ = useQuery({
    queryKey: ['epc-est-total', ctx?.estimateId, ctx?.revisionId],
    queryFn: () => epcEstimationService.totalCostSummary(ctx!.estimateId, ctx!.revisionId),
    enabled,
  });
  const durationQ = useQuery({
    queryKey: ['epc-est-duration', ctx?.estimateId, ctx?.revisionId],
    queryFn: () => epcEstimationService.getDuration(ctx!.estimateId, ctx!.revisionId),
    enabled,
  });
  const indirectQ = useQuery({
    queryKey: ['epc-est-indirect', ctx?.estimateId, ctx?.revisionId],
    queryFn: () => epcEstimationService.listIndirectCosts(ctx!.estimateId, ctx!.revisionId),
    enabled,
  });
  const riskQ = useQuery({
    queryKey: ['epc-est-risk', ctx?.estimateId, ctx?.revisionId],
    queryFn: () => epcEstimationService.listRiskLinks(ctx!.estimateId, ctx!.revisionId),
    enabled,
  });
  const contQ = useQuery({
    queryKey: ['epc-est-cont', ctx?.estimateId, ctx?.revisionId],
    queryFn: () => epcEstimationService.getContingency(ctx!.estimateId, ctx!.revisionId),
    enabled,
  });
  const escQ = useQuery({
    queryKey: ['epc-est-esc', ctx?.estimateId, ctx?.revisionId],
    queryFn: () => epcEstimationService.listEscalationRules(ctx!.estimateId, ctx!.revisionId),
    enabled,
  });
  const fxQ = useQuery({
    queryKey: ['epc-est-fx', ctx?.estimateId, ctx?.revisionId],
    queryFn: () => epcEstimationService.getFxAllowance(ctx!.estimateId, ctx!.revisionId),
    enabled,
  });
  return { totalQ, durationQ, indirectQ, riskQ, contQ, escQ, fxQ };
}

export function Wave5OverviewHero({
  total,
  costModel,
}: {
  total: any;
  costModel?: string;
}) {
  if (!total) {
    return <Alert type="info" showIcon message="Total Estimated Cost loads with a revision." />;
  }
  const pct = total.structure_pct || {};
  return (
    <Card size="small" style={{ marginBottom: 12 }}>
      <Row gutter={16} align="middle">
        <Col flex="auto">
          <Text type="secondary">Total Estimated Cost</Text>
          <Title level={3} style={{ margin: 0 }}>
            {money(total.total_estimated_cost)} {total.currency || ''}
          </Title>
          <Space wrap style={{ marginTop: 8 }}>
            <Tag>Direct {pct.direct ?? 0}%</Tag>
            <Tag>Indirect {pct.indirect ?? 0}%</Tag>
            <Tag>Risk {pct.risk ?? 0}%</Tag>
            <Tag>Contingency {pct.contingency ?? 0}%</Tag>
            <Tag>Escalation {pct.escalation ?? 0}%</Tag>
            <Tag>FX {pct.fx ?? 0}%</Tag>
          </Space>
        </Col>
        <Col>
          <Tag color={costModel === 'READY' || total.cost_model === 'READY' ? 'green' : 'orange'}>
            Cost Model {costModel || total.cost_model || '…'}
          </Tag>
          <Tag>Commercial Pricing NOT STARTED</Tag>
        </Col>
      </Row>
      <Table
        size="small"
        style={{ marginTop: 12 }}
        pagination={false}
        rowKey="k"
        dataSource={[
          { k: 'd', label: 'Direct', amt: total.direct_cost },
          { k: 'i', label: 'Indirect', amt: total.indirect_cost },
          { k: 'r', label: 'Risk Allowance', amt: total.risk_allowance },
          { k: 'c', label: 'Contingency', amt: total.contingency },
          { k: 'e', label: 'Escalation', amt: total.escalation },
          { k: 'f', label: 'FX Allowance', amt: total.fx_allowance },
        ]}
        columns={[
          { title: 'Bucket', dataIndex: 'label' },
          { title: 'Amount', dataIndex: 'amt', render: money, width: 140 },
        ]}
      />
      {(total.warnings || []).length > 0 && (
        <Alert
          style={{ marginTop: 12 }}
          type="warning"
          showIcon
          message={(total.warnings as any[]).map((w) => w.message).join(' · ')}
        />
      )}
    </Card>
  );
}

export function Wave5DurationPanel({ ctx }: { ctx: Wave5Ctx }) {
  const qc = useQueryClient();
  const { durationQ, totalQ } = useWave5Queries(ctx);
  const [form] = Form.useForm();
  const m = useMutation({
    mutationFn: (v: Record<string, unknown>) =>
      epcEstimationService.patchDuration(ctx.estimateId, ctx.revisionId, v),
    onSuccess: () => {
      message.success('Duration saved');
      qc.invalidateQueries({ queryKey: ['epc-est-duration'] });
      qc.invalidateQueries({ queryKey: ['epc-est-total'] });
      qc.invalidateQueries({ queryKey: ['epc-est-indirect'] });
      qc.invalidateQueries({ queryKey: ['epc-est-readiness'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Failed'),
  });
  React.useEffect(() => {
    if (durationQ.data) form.setFieldsValue(durationQ.data);
  }, [durationQ.data, form]);
  return (
    <Card size="small">
      {ctx.readOnly ? (
        <Alert type="info" showIcon message="Approved — duration read-only" style={{ marginBottom: 12 }} />
      ) : (
        <Form
          form={form}
          layout="inline"
          onFinish={(v) => m.mutate(v)}
          style={{ marginBottom: 12 }}
        >
          <Form.Item name="client_required_months" label="Client required (mo)">
            <InputNumber min={0} step={0.5} />
          </Form.Item>
          <Form.Item name="estimate_assumed_months" label="Estimate assumed (mo)">
            <InputNumber min={0} step={0.5} />
          </Form.Item>
          <Form.Item name="overall_duration_months" label="Overall (mo)">
            <InputNumber min={0} step={0.5} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={m.isPending}>
            Save
          </Button>
        </Form>
      )}
      <Space>
        <Tag>
          Variance{' '}
          {totalQ.data?.duration_summary?.variance_months ??
            durationQ.data?.variance_months ??
            '—'}{' '}
          mo
        </Tag>
        <Tag>Indirect total {money(totalQ.data?.indirect_cost)}</Tag>
      </Space>
    </Card>
  );
}

const METHOD_OPTIONS = [
  { value: 'FIXED_AMOUNT', label: 'Fixed amount' },
  { value: 'QUANTITY_X_RATE', label: 'Qty × Rate' },
  { value: 'MONTHLY_X_DURATION', label: 'Monthly × Duration' },
  { value: 'RESOURCE_X_DURATION', label: 'Resource × Duration' },
  { value: 'PERCENT_OF_BASE', label: '% of base' },
];

const BASE_OPTIONS = [
  { value: 'DIRECT_COST', label: 'Direct cost' },
  { value: 'DIRECT_PLUS_INDIRECT', label: 'Direct + Indirect (non-%)' },
  { value: 'SELECTED_WBS_DIRECT_COST', label: 'Selected WBS direct' },
  { value: 'SELECTED_CBS_DIRECT_COST', label: 'Selected CBS direct' },
];

export function Wave5IndirectsPanel({
  ctx,
  onOpenDuration,
}: {
  ctx: Wave5Ctx;
  onOpenDuration?: () => void;
}) {
  const qc = useQueryClient();
  const { indirectQ } = useWave5Queries(ctx);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [editRow, setEditRow] = useState<Record<string, unknown> | null>(null);
  const addMethod = Form.useWatch('calculation_method', form);
  const editMethod = Form.useWatch('calculation_method', editForm);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['epc-est-indirect'] });
    void qc.invalidateQueries({ queryKey: ['epc-est-total'] });
    void qc.invalidateQueries({ queryKey: ['epc-est-readiness'] });
    void qc.invalidateQueries({ queryKey: ['epc-est-pricing-sum'] });
  };

  const createM = useMutation({
    mutationFn: (v: Record<string, unknown>) =>
      epcEstimationService.createIndirectCost(ctx.estimateId, {
        revisionId: ctx.revisionId,
        ...v,
      }),
    onSuccess: () => {
      message.success('Indirect line added');
      form.resetFields();
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Failed'),
  });

  const patchM = useMutation({
    mutationFn: (v: Record<string, unknown>) =>
      epcEstimationService.patchIndirectCost(ctx.estimateId, String(editRow!.id), {
        revisionId: ctx.revisionId,
        ...v,
      }),
    onSuccess: () => {
      message.success('Indirect line updated');
      setEditRow(null);
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Failed'),
  });

  const deleteM = useMutation({
    mutationFn: (lineId: string) =>
      epcEstimationService.deleteIndirectCost(ctx.estimateId, lineId, ctx.revisionId),
    onSuccess: () => {
      message.success('Indirect line removed');
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Failed'),
  });

  const openEdit = (row: Record<string, unknown>) => {
    setEditRow(row);
    editForm.setFieldsValue({
      category: row.category,
      description: row.description,
      calculation_method: row.calculation_method,
      fixed_amount: row.fixed_amount,
      quantity: row.quantity,
      rate: row.rate,
      percentage: row.percentage,
      base_selector: row.base_selector,
      duration_months: row.duration_months,
    });
  };

  const durationHint =
    addMethod === 'MONTHLY_X_DURATION' ||
    addMethod === 'RESOURCE_X_DURATION' ||
    editMethod === 'MONTHLY_X_DURATION' ||
    editMethod === 'RESOURCE_X_DURATION';

  return (
    <Card size="small">
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Indirect / prelim costs feed TEC before commercial markup or margin."
      />
      {durationHint && onOpenDuration ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Duration-based methods use estimate duration assumptions"
          action={
            <Button size="small" type="link" onClick={onOpenDuration}>
              Edit duration
            </Button>
          }
        />
      ) : null}
      {!ctx.readOnly && (
        <Form
          form={form}
          layout="vertical"
          onFinish={(v) => createM.mutate(v)}
          style={{ marginBottom: 12 }}
          initialValues={{
            category: 'SITE_ESTABLISHMENT',
            calculation_method: 'FIXED_AMOUNT',
            fixed_amount: 0,
            rate: 0,
            base_selector: 'DIRECT_COST',
          }}
        >
          <Row gutter={8}>
            <Col xs={24} md={8}>
              <Form.Item name="category" label="Category" rules={[{ required: true }]}>
                <Select options={INDIRECT_CATEGORIES.map((c) => ({ value: c, label: c }))} />
              </Form.Item>
            </Col>
            <Col xs={24} md={10}>
              <Form.Item name="description" label="Description" rules={[{ required: true }]}>
                <Input placeholder="Description" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="calculation_method" label="Method">
                <Select options={METHOD_OPTIONS} />
              </Form.Item>
            </Col>
            {addMethod === 'FIXED_AMOUNT' || !addMethod ? (
              <Col xs={12} md={6}>
                <Form.Item name="fixed_amount" label="Fixed amount">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            ) : null}
            {addMethod === 'QUANTITY_X_RATE' ||
            addMethod === 'MONTHLY_X_DURATION' ||
            addMethod === 'RESOURCE_X_DURATION' ? (
              <>
                <Col xs={12} md={6}>
                  <Form.Item name="quantity" label="Quantity">
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Item name="rate" label="Rate">
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </>
            ) : null}
            {addMethod === 'PERCENT_OF_BASE' ? (
              <>
                <Col xs={12} md={6}>
                  <Form.Item name="percentage" label="% (whole)">
                    <InputNumber min={0} max={100} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={8}>
                  <Form.Item name="base_selector" label="Base">
                    <Select options={BASE_OPTIONS} />
                  </Form.Item>
                </Col>
              </>
            ) : null}
            <Col xs={24} md={6} style={{ display: 'flex', alignItems: 'end' }}>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={createM.isPending}>
                  Add indirect
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      )}
      <Table
        size="small"
        rowKey="id"
        loading={indirectQ.isLoading}
        dataSource={indirectQ.data || []}
        pagination={false}
        scroll={{ x: 880 }}
        columns={[
          {
            title: 'Category',
            dataIndex: 'category',
            width: 200,
            ellipsis: true,
            render: (v: unknown) => String(v || '').replace(/_/g, ' '),
          },
          { title: 'Description', dataIndex: 'description', width: 240, ellipsis: true },
          {
            title: 'Method',
            dataIndex: 'calculation_method',
            width: 170,
            ellipsis: true,
            render: (v: unknown) => String(v || '').replace(/_/g, ' '),
          },
          {
            title: 'Amount',
            dataIndex: 'calculated_amount',
            width: 140,
            align: 'right' as const,
            render: money,
          },
          {
            title: '',
            key: 'actions',
            width: 130,
            fixed: 'right' as const,
            render: (_: unknown, row: any) =>
              ctx.readOnly ? null : (
                <Space size={4}>
                  <Button size="small" type="link" onClick={() => openEdit(row)}>
                    Edit
                  </Button>
                  <Popconfirm
                    title="Remove this indirect line?"
                    onConfirm={() => deleteM.mutate(String(row.id))}
                  >
                    <Button size="small" type="link" danger loading={deleteM.isPending}>
                      Delete
                    </Button>
                  </Popconfirm>
                </Space>
              ),
          },
        ]}
      />

      <Drawer
        title="Edit indirect cost"
        open={!!editRow}
        onClose={() => setEditRow(null)}
        width={420}
        destroyOnClose
        extra={
          !ctx.readOnly ? (
            <Button type="primary" loading={patchM.isPending} onClick={() => editForm.submit()}>
              Save
            </Button>
          ) : null
        }
      >
        <Form form={editForm} layout="vertical" onFinish={(v) => patchM.mutate(v)}>
          <Form.Item name="category" label="Category" rules={[{ required: true }]}>
            <Select options={INDIRECT_CATEGORIES.map((c) => ({ value: c, label: c }))} />
          </Form.Item>
          <Form.Item name="description" label="Description" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="calculation_method" label="Method">
            <Select options={METHOD_OPTIONS} />
          </Form.Item>
          <Form.Item name="fixed_amount" label="Fixed amount">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="quantity" label="Quantity">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="rate" label="Rate">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="percentage" label="% (whole)">
            <InputNumber min={0} max={100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="base_selector" label="Percent base">
            <Select allowClear options={BASE_OPTIONS} />
          </Form.Item>
          <Form.Item name="duration_months" label="Duration months (override)">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          {onOpenDuration ? (
            <Button type="link" style={{ padding: 0 }} onClick={onOpenDuration}>
              Open duration assumptions
            </Button>
          ) : null}
        </Form>
      </Drawer>
    </Card>
  );
}

export function Wave5RisksPanel({ ctx }: { ctx: Wave5Ctx }) {
  const qc = useQueryClient();
  const { riskQ, totalQ } = useWave5Queries(ctx);
  const [form] = Form.useForm();
  const m = useMutation({
    mutationFn: (v: Record<string, unknown>) =>
      epcEstimationService.createRiskLink(ctx.estimateId, { revisionId: ctx.revisionId, ...v }),
    onSuccess: () => {
      message.success('Risk added');
      form.resetFields();
      qc.invalidateQueries({ queryKey: ['epc-est-risk'] });
      qc.invalidateQueries({ queryKey: ['epc-est-total'] });
      qc.invalidateQueries({ queryKey: ['epc-est-readiness'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Failed'),
  });
  const rs = totalQ.data?.risk_summary;
  return (
    <Card size="small">
      <Space style={{ marginBottom: 12 }}>
        <Tag>Identified {rs?.identified_count ?? (riskQ.data || []).length}</Tag>
        <Tag color="green">Priced {money(rs?.priced_allowance ?? totalQ.data?.risk_allowance)}</Tag>
        <Tag color="orange">Unpriced {rs?.unpriced_count ?? 0}</Tag>
      </Space>
      {!ctx.readOnly && (
        <Form form={form} layout="inline" onFinish={(v) => m.mutate(v)} style={{ marginBottom: 12 }}>
          <Form.Item name="title" rules={[{ required: true }]}>
            <Input placeholder="Risk title" style={{ width: 180 }} />
          </Form.Item>
          <Form.Item name="probability_pct" initialValue={40}>
            <InputNumber placeholder="Prob %" min={0} max={100} />
          </Form.Item>
          <Form.Item name="cost_impact" initialValue={0}>
            <InputNumber placeholder="Impact" min={0} />
          </Form.Item>
          <Form.Item name="treatment" initialValue="NOT_PRICED">
            <Select
              style={{ width: 180 }}
              options={RISK_TREATMENTS.map((t) => ({ value: t, label: t }))}
            />
          </Form.Item>
          <Form.Item name="priced_allowance">
            <InputNumber placeholder="Priced allce" min={0} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={m.isPending}>
            Add
          </Button>
        </Form>
      )}
      <Table
        size="small"
        rowKey="id"
        loading={riskQ.isLoading}
        dataSource={riskQ.data || []}
        pagination={false}
        columns={[
          { title: 'Title', dataIndex: 'title' },
          { title: 'P%', dataIndex: 'probability_pct', width: 60 },
          { title: 'Impact', dataIndex: 'cost_impact', width: 100, render: money },
          { title: 'EMV', dataIndex: 'emv_amount', width: 100, render: money },
          { title: 'Treatment', dataIndex: 'treatment', width: 160 },
          { title: 'Priced', dataIndex: 'priced_allowance', width: 100, render: money },
        ]}
      />
    </Card>
  );
}

export function Wave5ContingencyPanel({ ctx }: { ctx: Wave5Ctx }) {
  const qc = useQueryClient();
  const { contQ } = useWave5Queries(ctx);
  const [form] = Form.useForm();
  const m = useMutation({
    mutationFn: (v: Record<string, unknown>) =>
      epcEstimationService.patchContingency(ctx.estimateId, ctx.revisionId, v),
    onSuccess: () => {
      message.success('Contingency saved');
      qc.invalidateQueries({ queryKey: ['epc-est-cont'] });
      qc.invalidateQueries({ queryKey: ['epc-est-total'] });
      qc.invalidateQueries({ queryKey: ['epc-est-readiness'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Failed'),
  });
  React.useEffect(() => {
    if (contQ.data) form.setFieldsValue(contQ.data);
  }, [contQ.data, form]);
  return (
    <Card size="small">
      {ctx.readOnly ? (
        <Alert type="info" showIcon message="Approved — contingency read-only" />
      ) : (
        <Form form={form} layout="vertical" onFinish={(v) => m.mutate(v)} style={{ maxWidth: 480 }}>
          <Form.Item name="method" label="Method" initialValue="PERCENT_DIRECT">
            <Select
              options={[
                { value: 'FIXED_AMOUNT', label: 'Fixed amount' },
                { value: 'PERCENT_DIRECT', label: '% of Direct' },
                { value: 'PERCENT_SELECTED_COST', label: '% selected base' },
                { value: 'MANUAL_APPROVED', label: 'Manual approved' },
              ]}
            />
          </Form.Item>
          <Form.Item name="basis_selector" label="Basis" initialValue="DIRECT_COST">
            <Select
              options={[
                { value: 'DIRECT_COST', label: 'Direct' },
                { value: 'DIRECT_PLUS_INDIRECT', label: 'Direct + Indirect' },
              ]}
            />
          </Form.Item>
          <Form.Item name="rate_pct" label="Rate % (whole)">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="override_amount" label="Override amount">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="override_reason" label="Override reason">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Text type="secondary">
            Calculated: {money(contQ.data?.calculated_amount)} · Effective uses override when set
          </Text>
          <div style={{ marginTop: 12 }}>
            <Button type="primary" htmlType="submit" loading={m.isPending}>
              Save contingency
            </Button>
          </div>
        </Form>
      )}
    </Card>
  );
}

export function Wave5EscalationPanel({ ctx }: { ctx: Wave5Ctx }) {
  const qc = useQueryClient();
  const { escQ } = useWave5Queries(ctx);
  const [form] = Form.useForm();
  const m = useMutation({
    mutationFn: (v: Record<string, unknown>) =>
      epcEstimationService.createEscalationRule(ctx.estimateId, {
        revisionId: ctx.revisionId,
        ...v,
      }),
    onSuccess: () => {
      message.success('Escalation rule added');
      form.resetFields();
      qc.invalidateQueries({ queryKey: ['epc-est-esc'] });
      qc.invalidateQueries({ queryKey: ['epc-est-total'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Failed'),
  });
  return (
    <Card size="small">
      {!ctx.readOnly && (
        <Form form={form} layout="inline" onFinish={(v) => m.mutate(v)} style={{ marginBottom: 12 }}>
          <Form.Item name="category" rules={[{ required: true }]}>
            <Input placeholder="Category" style={{ width: 140 }} />
          </Form.Item>
          <Form.Item name="method" initialValue="FIXED_PERCENT">
            <Select
              style={{ width: 140 }}
              options={[
                { value: 'FIXED_PERCENT', label: 'Fixed %' },
                { value: 'ANNUAL_RATE', label: 'Annual' },
                { value: 'CATEGORY_RATE', label: 'Category' },
              ]}
            />
          </Form.Item>
          <Form.Item name="rate_pct" initialValue={1}>
            <InputNumber placeholder="Rate %" min={0} />
          </Form.Item>
          <Form.Item name="scope_type" initialValue="WHOLE_ESTIMATE">
            <Select
              style={{ width: 140 }}
              options={[
                { value: 'WHOLE_ESTIMATE', label: 'Whole' },
                { value: 'WBS', label: 'WBS' },
                { value: 'CBS', label: 'CBS' },
                { value: 'COST_TYPE', label: 'Cost type' },
              ]}
            />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={m.isPending}>
            Add
          </Button>
        </Form>
      )}
      <Table
        size="small"
        rowKey="id"
        loading={escQ.isLoading}
        dataSource={escQ.data || []}
        pagination={false}
        columns={[
          { title: 'Category', dataIndex: 'category' },
          { title: 'Method', dataIndex: 'method', width: 120 },
          { title: 'Rate %', dataIndex: 'rate_pct', width: 80 },
          { title: 'Scope', dataIndex: 'scope_type', width: 120 },
          { title: 'Amount', dataIndex: 'calculated_amount', width: 110, render: money },
        ]}
      />
    </Card>
  );
}

export function Wave5FxPanel({ ctx }: { ctx: Wave5Ctx }) {
  const qc = useQueryClient();
  const { fxQ } = useWave5Queries(ctx);
  const [form] = Form.useForm();
  const m = useMutation({
    mutationFn: (v: Record<string, unknown>) =>
      epcEstimationService.patchFxAllowance(ctx.estimateId, ctx.revisionId, v),
    onSuccess: () => {
      message.success('FX allowance saved');
      qc.invalidateQueries({ queryKey: ['epc-est-fx'] });
      qc.invalidateQueries({ queryKey: ['epc-est-total'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Failed'),
  });
  const allowance = fxQ.data?.allowance;
  const exposure = fxQ.data?.exposure;
  React.useEffect(() => {
    if (allowance) form.setFieldsValue(allowance);
  }, [allowance, form]);
  return (
    <Card size="small">
      <Space style={{ marginBottom: 12 }}>
        <Tag>Foreign exposure {money(exposure?.foreign_exposure_base)}</Tag>
        <Tag>Allowance {money(allowance?.calculated_amount)}</Tag>
      </Space>
      {(exposure?.by_currency || []).length > 0 && (
        <Table
          size="small"
          style={{ marginBottom: 12 }}
          rowKey="currency"
          pagination={false}
          dataSource={exposure.by_currency}
          columns={[
            { title: 'Currency', dataIndex: 'currency' },
            {
              title: 'Amount (base)',
              dataIndex: 'amount_base',
              render: money,
            },
            {
              title: 'Foreign',
              dataIndex: 'is_foreign',
              render: (v: boolean) => (v ? 'Yes' : 'No'),
              width: 80,
            },
          ]}
        />
      )}
      {!ctx.readOnly && (
        <Form form={form} layout="vertical" onFinish={(v) => m.mutate(v)} style={{ maxWidth: 420 }}>
          <Form.Item name="method" label="Method" initialValue="PERCENT_EXPOSURE">
            <Select
              options={[
                { value: 'FIXED_AMOUNT', label: 'Fixed' },
                { value: 'PERCENT_EXPOSURE', label: '% of foreign exposure' },
                { value: 'SCENARIO_DELTA', label: 'Scenario delta' },
              ]}
            />
          </Form.Item>
          <Form.Item name="rate_pct" label="Rate % (whole)">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="fixed_amount" label="Fixed amount">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={m.isPending}>
            Save FX allowance
          </Button>
        </Form>
      )}
    </Card>
  );
}
